import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import { retry } from '@reduxjs/toolkit/query';
import { baseRestApi } from '@src/utils/rtkQuery/baseRestApi';
import type { ErrorResponse } from '@talos/kyoko';
import type {
  AssetMetricCatalogEntry,
  AssetMetricPoint,
  AssetMetricReferenceEntry,
  AssetMetricsCatalogParams,
  AssetMetricsParams,
  AssetReferenceEntry,
  AssetReferenceParams,
  CoinMetricsDataResponse,
  CoinMetricsError,
  CoinMetricsQueryArgs,
  ExchangeAssetMetricCatalogEntry,
  ExchangeAssetMetricPoint,
  ExchangeAssetMetricsCatalogParams,
  ExchangeAssetMetricsParams,
  ExchangeReferenceEntry,
  ExchangeReferenceParams,
} from './coinMetrics.types';
import { coinMetricsBaseQuery } from './coinMetricsBaseQuery';
import { setCoinMetricsApiKeyInvalid } from './coinMetricsCredentialsSlice';
import { isCoinMetricsAuthError, isCoinMetricsForbiddenError } from './coinMetricsErrors';

const COIN_METRICS_MAX_RETRIES = 2;

const coinMetricsBaseQueryWithRetry = retry(coinMetricsBaseQuery, {
  // RTK types `maxRetries` and `retryCondition` as mutually exclusive, so the retry cap lives inside
  // the condition. Bail immediately on an invalid API key or a per-resource entitlement gap (403) —
  // both are deterministic and never become retryable with the same key — so the error/permission
  // message surfaces now instead of after backoff; retry everything else up to the cap.
  retryCondition: (error, _args, { attempt }) => {
    const cmError = error as CoinMetricsError;
    return (
      attempt <= COIN_METRICS_MAX_RETRIES && !isCoinMetricsAuthError(cmError) && !isCoinMetricsForbiddenError(cmError)
    );
  },
});

/** Converts a CoinMetricsError into a valid ErrorResponse so toasts and error handlers work correctly. */
function toErrorResponse(cmError: CoinMetricsError): ErrorResponse {
  // Fold the CM-specific type (e.g. 'bad_parameter') into the message so diagnostic info is preserved.
  const message = cmError.type ? `[${cmError.type}] ${cmError.message}` : cmError.message;
  return { type: 'error', ts: new Date().toISOString(), message };
}

/**
 * Runs a single CoinMetrics request through the shared retry/auth-invalidation wrapper. Exported so
 * callers that need to fan out multiple CoinMetrics requests from inside their own `queryFn` (e.g. the
 * Asset Leaderboard's batched snapshot endpoint) can reuse the same retry and API-key-validity handling
 * per request, without duplicating it or going through a second, redundant RTK Query cache entry per
 * request via `initiate`.
 */
export async function runCoinMetricsQuery<T>(
  args: CoinMetricsQueryArgs,
  api: BaseQueryApi
): Promise<{ data: T } | { error: ErrorResponse }> {
  const result = await coinMetricsBaseQueryWithRetry(args, api, {});
  // Value check (not `'error' in result`) so `result.error` narrows to CoinMetricsError without a cast.
  if (result.error != null) {
    const cmError = result.error;
    // Track key validity globally so every Intel module can show a dedicated "invalid API key"
    // message. Only pro requests carry the key; a non-auth error means the key authenticated fine.
    if (args.tier === 'pro') {
      api.dispatch(setCoinMetricsApiKeyInvalid(isCoinMetricsAuthError(cmError)));
    }
    return { error: toErrorResponse(cmError) };
  }
  if (args.tier === 'pro') {
    api.dispatch(setCoinMetricsApiKeyInvalid(false));
  }
  return result as { data: T };
}

/**
 * Central registry for all CoinMetrics API endpoints.
 *
 * These endpoints are injected into the shared baseRestApi slice (convention: single non-streaming
 * createApi instance). Each endpoint uses queryFn + coinMetricsBaseQuery so requests go to the
 * CoinMetrics external API (raw fetch, credentials: omit, CM base URLs) and never through the
 * Talos restEndpointBaseQuery.
 *
 * Conventions:
 * - Group endpoints by data domain with a comment block.
 * - Declare `tier` per endpoint — never rely on a caller to know which tier an endpoint requires.
 * - `keepUnusedDataFor` is tuned per endpoint type:
 *     - Historical time-series  → 600s   (large, expensive, changes slowly)
 *     - Catalog                 → 3600s  (which assets/pairs support a metric — can gain new
 *                                         entries as CoinMetrics onboards coverage)
 *     - Reference data          → 86400s (id/name/unit metadata — effectively static day-to-day,
 *                                         so a whole day of staleness is an acceptable tradeoff)
 * - Every endpoint declares `providesTags: ['CoinMetrics']` so `CoinMetricsCredentialsSyncer` can
 *   invalidate all of them in one shot when the org's API key changes — otherwise a long-lived
 *   catalog/reference entry cached under a bad key would keep serving that stale result for up to
 *   `keepUnusedDataFor` after the key is fixed.
 *
 * Skip guards (apply at the hook call-site, not here):
 *   - Community endpoints: no skip needed — base URL is always known.
 *   - Pro endpoints:       skip: !apiKey
 */
const coinMetricsEndpointsInternal = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    // ── Asset Metrics ─────────────────────────────────────────────────────────
    // All asset-metrics endpoints run on the pro tier: the consuming features are gated
    // on a configured CoinMetrics API key, and the community tier returns a truncated
    // universe (fewer assets, fewer metrics, shorter history — e.g. CapMrktEstUSD only
    // from mid-2019). Pro tier with the key returns full entitlements.
    // Timeseries: GET /timeseries/asset-metrics   (pro) — historical OHLCV/metric values
    // Catalog:    GET /catalog-v2/asset-metrics   (pro) — which assets support which metrics and over what time range
    // Reference:  GET /reference-data/asset-metrics (pro) — human-readable metric definitions

    getAssetMetrics: builder.query<CoinMetricsDataResponse<AssetMetricPoint>, AssetMetricsParams>({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<AssetMetricPoint>>(
          // Timeseries can span long date ranges — cap at 20 pages (2 000 records at default page_size).
          // Callers should scope queries with start_time/end_time to stay well under this.
          { url: '/timeseries/asset-metrics', params, tier: 'pro', maxPages: 20 },
          api
        ),
      keepUnusedDataFor: 600,
      providesTags: ['CoinMetrics'],
    }),

    getAssetMetricsCatalog: builder.query<CoinMetricsDataResponse<AssetMetricCatalogEntry>, AssetMetricsCatalogParams>({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<AssetMetricCatalogEntry>>(
          // Catalog responses are compact metadata; 10 pages is a generous ceiling.
          { url: '/catalog-v2/asset-metrics', params, tier: 'pro', maxPages: 10 },
          api
        ),
      keepUnusedDataFor: 3600,
      providesTags: ['CoinMetrics'],
    }),

    getAssetMetricsReference: builder.query<
      CoinMetricsDataResponse<AssetMetricReferenceEntry>,
      AssetMetricsCatalogParams
    >({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<AssetMetricReferenceEntry>>(
          // Reference data is compact metric definitions; 10 pages is a generous ceiling.
          { url: '/reference-data/asset-metrics', params, tier: 'pro', maxPages: 10 },
          api
        ),
      keepUnusedDataFor: 86400,
      providesTags: ['CoinMetrics'],
    }),

    // ── Assets Reference ──────────────────────────────────────────────────────
    // Reference: GET /reference-data/assets (pro) — asset metadata + cross-namespace
    // identifiers (e.g. Talos symbol when include=talos is passed). Used to translate
    // CM asset IDs -> Talos Currency.Symbol so callers can present Talos-native dropdowns.

    getAssetsReference: builder.query<CoinMetricsDataResponse<AssetReferenceEntry>, AssetReferenceParams>({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<AssetReferenceEntry>>(
          // Reference data is compact metadata; 10 pages is a generous ceiling.
          { url: '/reference-data/assets', params, tier: 'pro', maxPages: 10 },
          api
        ),
      keepUnusedDataFor: 86400,
      providesTags: ['CoinMetrics'],
    }),

    // ── Exchanges Reference ───────────────────────────────────────────────────
    // Reference: GET /reference-data/exchanges (pro) — exchange metadata + cross-namespace
    // identifiers (e.g. Talos display_name when include=talos is passed). Used to show
    // Talos-native exchange names in Intel instead of raw CoinMetrics exchange ids.

    getExchangesReference: builder.query<CoinMetricsDataResponse<ExchangeReferenceEntry>, ExchangeReferenceParams>({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<ExchangeReferenceEntry>>(
          // Reference data is compact metadata; 10 pages is a generous ceiling.
          { url: '/reference-data/exchanges', params, tier: 'pro', maxPages: 10 },
          api
        ),
      keepUnusedDataFor: 86400,
      providesTags: ['CoinMetrics'],
    }),

    // ── Exchange-Asset Metrics ────────────────────────────────────────────────────
    // Both endpoints run on the pro tier.
    // Timeseries: GET /timeseries/exchange-asset-metrics — historical values per (exchange, asset) pair
    // Catalog:    GET /catalog-v2/exchange-asset-metrics — which (exchange, asset) pairs support which metrics

    getExchangeAssetMetrics: builder.query<
      CoinMetricsDataResponse<ExchangeAssetMetricPoint>,
      ExchangeAssetMetricsParams
    >({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<ExchangeAssetMetricPoint>>(
          // Timeseries can span long date ranges — cap at 20 pages. Callers should scope
          // queries with start_time/end_time to stay well under this.
          { url: '/timeseries/exchange-asset-metrics', params, tier: 'pro', maxPages: 20 },
          api
        ),
      keepUnusedDataFor: 600,
      providesTags: ['CoinMetrics'],
    }),

    getExchangeAssetMetricsCatalog: builder.query<
      CoinMetricsDataResponse<ExchangeAssetMetricCatalogEntry>,
      ExchangeAssetMetricsCatalogParams
    >({
      queryFn: (params, api) =>
        runCoinMetricsQuery<CoinMetricsDataResponse<ExchangeAssetMetricCatalogEntry>>(
          // Catalog responses are compact metadata filtered by metric + wildcard; 10 pages is generous.
          { url: '/catalog-v2/exchange-asset-metrics', params, tier: 'pro', maxPages: 10 },
          api
        ),
      keepUnusedDataFor: 3600,
      providesTags: ['CoinMetrics'],
    }),
  }),
});

export const {
  useGetAssetMetricsQuery,
  useLazyGetAssetMetricsQuery,
  useGetAssetMetricsCatalogQuery,
  useGetAssetMetricsReferenceQuery,
  useGetAssetsReferenceQuery,
  useGetExchangeAssetMetricsQuery,
  useGetExchangeAssetMetricsCatalogQuery,
  useGetExchangesReferenceQuery,
} = coinMetricsEndpointsInternal;
